
import React, { useState } from "react";
import { Formik, Form, FormikProps } from "formik";
import { Divider, Message, Form as SForm, Segment } from "@/components/bp/bp-ui";
import * as yup from "yup";
import { FormErrorList } from "../FormErrorList";
import SemanticFormikInput from "@app/components/React/Common/Forms/SemanticFormikInput";
import SemanticFormikSelect from "@app/components/React/Common/Forms/SemanticFormikSelect";
import { PageHeader } from "@common/PageHeader";
import { replaceInString } from "@common/Utils";
import { userClient, roleClient, translationClient } from "@app/api2/Clients";
import { buildUrl, AppRoutes } from "../AppRoutes";
import { useQuery } from "@tanstack/react-query";
import { useTranslation2} from "../Contexts/TranslationContext2";
import { BPButton } from "@/components/bp/bpbutton";
import { BPContainer } from "@/components/bp/bpcontainer";


// Types
interface IUserEditProps {
    userId: string;
}

class UserEditFormData {
    public name: string = "";
    public email: string = "";
    public role: string = "";
    public lab: number = 0;
    public locale: string = "";
}

export const UserEditFunctional: React.FC<IUserEditProps> = ({ userId }) => {
    // State
    const [showServerErrors, setShowServerErrors] = useState(false);
    const [serverErrors, setServerErrors] = useState<string[]>([]);

    // Context
    // 
    // const t = useContext(translationsContext);
    const { translations: t } = useTranslation2();

    // Validation Schema
    const UserEditSchema: yup.SchemaOf<UserEditFormData> = yup.object().shape({
        email: yup.string().email().required().label(t.common.emailAddress),
        name: yup.string().required().label(t.user.name),
        lab: yup.number(),
        locale: yup.string().required(),
        role: yup.string(),
    });

    const query = useQuery({
        queryKey: ['userEdit', userId],
        queryFn: async () => {
            const locales = await translationClient.getLocales();
            const user  = await userClient.get(userId);
            const roles = await roleClient.getAll();
            return { locales, user, roles };
        }
    });

    const user = query.data?.user;
    const roleList = query.data?.roles?.map((r) => ({ text: r.name, value: r.name, key: r.name })) || [];
    const localeList = query.data?.locales?.map((l) => ({ text: l.name, value: l.code, key: l.code })) || [];

    // Handlers
    const handleSubmit = async (values: UserEditFormData) => {
        // const userClient = new UserClient();
        const result = await userClient.update(userId, {
            email: values.email,
            id: userId,
            labs: [],
            name: values.name,
            role: values.role,
            locale: values.locale,
            isActive: true,
        });
        if (result.success) {
            window.location.assign(buildUrl(AppRoutes.UserDetails, { userId }));
        } else {
            setServerErrors(result.errors);
            setShowServerErrors(true);
        }
    };


    // Renderers
    const renderForm = (formikProps: FormikProps<UserEditFormData>) => {
        const errorList = Object.getOwnPropertyNames(formikProps.errors)
            .filter((k) => formikProps.touched[k])
            .map((k) => formikProps.errors[k]);
        return (
            <SForm as={Form} onSubmit={formikProps.handleSubmit}>
                <Segment loading={query.isLoading}>
                    <SemanticFormikInput name="name" label={t.user.name} required />
                    <SemanticFormikInput name="email" label={t.common.emailAddress} required />
                    <SemanticFormikSelect name="role" label={t.user.role} options={roleList} selection required />
                    <SemanticFormikSelect name="locale" label={t.common.language} options={localeList} selection required />
                    <Divider />
                    <BPButton type="submit" disabled={!formikProps.isValid}>{t.common.save}</BPButton>
                    <Message error header={t.common.errorsOnForm} list={errorList} visible={errorList.length > 0} />
                    <FormErrorList message={t.user.errorCreating} show={showServerErrors} errors={serverErrors} />
                </Segment>
            </SForm>
        );
    };

    // Main Render
    return (
        <BPContainer loading={query.isLoading}>
            <PageHeader header={replaceInString(t.userView.edit, { '{userName}': user?.name, '{email}': user?.email })} />
            {!query.isLoading && (
                <Formik
                    initialValues={{
                        ...user, role: user.roles[0] || '',
                        locale: user.locale || '',
                        canResendInitialEmail: false,
                        lab: user.labs[0]?.labId || 0,
                        active: user.active || false,
                        name: user.name,
                        email: user.email,
                    }}
                    isInitialValid={true}
                    onSubmit={handleSubmit}
                    validationSchema={UserEditSchema}
                    enableReinitialize>
                    {renderForm}
                </Formik>
            )}
        </BPContainer>
    );
};
